import { StudioType } from '@conversed/shared/src/central/types/studio.type';
import { LoadingSpinner } from '@conversed/ui/src/components/spinner/LoadingSpinner';
import type React from 'react';
import { Suspense, useMemo } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router';
import { SETTINGS_PAGE_ROUTE } from '../../constants/routes';
import { isRouteAllowedForStudioType } from '../../helpers/routeHelpers';
import { useCompanySelector } from '../../hooks/useCompanySelector';
import { usePermissions } from '../../hooks/usePermissions';
import { usePlatformFeatures } from '../../hooks/usePlatformFeatures';
import { SettingsSidebarRoutes } from '../routes/SettingsSidebarRoutes';

const useSettingsRoutes = () => {
  const { hasPermissions } = usePermissions();
  const { hasPlatformFeature } = usePlatformFeatures();
  const { selectedCompany } = useCompanySelector();

  const currentStudioType = selectedCompany?.studioType || StudioType.CHAT_AI;

  const routes = useMemo(
    () =>
      SettingsSidebarRoutes.flatMap((routeGroups) =>
        routeGroups.elements.map((route) => ({
          ...route,
          path: `${SETTINGS_PAGE_ROUTE}/${routeGroups.path}/${route.path}`,
        })),
      ).filter(
        (route) =>
          hasPermissions(route.permissions) &&
          (!route.platformFeature ||
            hasPlatformFeature(route.platformFeature)) &&
          isRouteAllowedForStudioType(route.studioTypes, currentStudioType),
      ),
    [hasPermissions, hasPlatformFeature, currentStudioType],
  );

  return routes;
};

export const SettingsSidebarRouter = () => {
  const routes = useSettingsRoutes();
  const location = useLocation();

  return (
    <div className='p-4 w-full min-w-min'>
      <Suspense fallback={<LoadingSpinner visible />}>
        {routes.map(({ path, component: Component }) => {
          if (location.pathname === path && Component) {
            return <Component key={path} />;
          }
          return null;
        })}
      </Suspense>
    </div>
  );
};

/**
 * Desktop only, and so is the redirect below: `SettingsPage` renders this component
 * only above the `md` breakpoint. On mobile it renders `SettingsSidebar` alone, so a
 * studio-restricted settings URL there still shows the sidebar rather than
 * redirecting.
 */
export const SettingsContent: React.FC = () => {
  const routes = useSettingsRoutes();
  const location = useLocation();
  const { selectedCompany } = useCompanySelector();

  // * The studio type falls back to CHAT_AI while the company is unresolved, so
  // * redirecting here would bounce an Email or Voice tenant's own deep link to a
  // * chat settings page before its route list is even known.
  if (!selectedCompany) {
    return <LoadingSpinner visible />;
  }

  // * SettingsSidebarRouter renders a route only on an exact pathname match, and a
  // * route the current studio type may not reach is dropped from the list
  // * entirely. So an unmatched pathname renders an empty panel - redirect to the
  // * first allowed page instead. `/settings` itself matches nothing either, which
  // * is how it keeps redirecting.
  const isPathAllowed = routes.some(
    (route) => route.path === location.pathname,
  );

  if (!isPathAllowed && routes.length) {
    return <Navigate to={routes[0].path} />;
  }

  return (
    <Routes>
      <Route path='*' element={<SettingsSidebarRouter />} />
    </Routes>
  );
};

export default SettingsContent;
